import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import type { CapiProductPage } from 'types/capi/pdp';

import Countdown from '~/components/Countdown';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import ProductNotificationCount from '~/components/ProductNotificationCount';
import { textColours } from '~/domain/stockStatus';
import useRegion from '~/hooks/useRegion';
import { formatReleaseDateLong } from '~/utils/dates';

type Props = {
  isNotified: boolean;
  product: CapiProductPage;
};

export default function NotificationCta({ product, isNotified }: Props) {
  return (
    <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4">
      {product.stock_status === 'sold-out' ? (
        <SoldOutStatus />
      ) : (
        <ComingSoonStatus product={product} />
      )}
      {product.allow_user_notifications && (
        <div className="flex flex-col gap-2">
          <ProductNotificationButton
            product={{
              id: product.product_id,
              image: product.gallery[0],
              legacyId: product.legacy_id,
              name: product.name,
              price: product.price.regular
            }}
            className="z-10 h-12 w-full text-center"
            pagePosition="WTB"
            buttonStyle="normal"
          />
          <Tooltip
            numOfNotifications={product.weekly_notifications_count}
            isNotified={isNotified}
          />
        </div>
      )}
    </div>
  );
}

function ComingSoonStatus({ product }: { product: CapiProductPage }) {
  const { t } = useTranslation();
  const region = useRegion();

  const isComingSoon = product.stock_status === 'coming-soon';
  const releaseDate = product.date.released;

  return (
    <div className="flex justify-between">
      <div>
        <div className={`text-base ${textColours['coming-soon']}`}>
          {t(product.stock_status)}
        </div>
        <div className="text-sm">
          {isComingSoon &&
            releaseDate &&
            formatReleaseDateLong(releaseDate, region)}
        </div>
      </div>
      {isComingSoon && releaseDate && (
        <ClientOnly>
          {() => <Countdown toUnixTimestamp={releaseDate} />}
        </ClientOnly>
      )}
    </div>
  );
}

function SoldOutStatus() {
  const { t } = useTranslation();

  return (
    <div className="flex justify-between">
      <div>
        <div className={`text-base ${textColours['sold-out']}`}>
          {t('sold-out')}
        </div>
      </div>
    </div>
  );
}

function Tooltip({
  numOfNotifications,
  isNotified
}: {
  isNotified: boolean;
  numOfNotifications?: number;
}) {
  const region = useRegion();
  const { t } = useTranslation();

  if (isNotified || region.code !== 'GB') return null;

  return (
    <div className="translate-y-[-120%] animate-slide-from-top text-clip">
      {numOfNotifications && numOfNotifications > 50 ? (
        <ProductNotificationCount count={numOfNotifications} />
      ) : (
        <div className="bg-purple-hola-lm p-2 text-center text-xs text-purple-hola">
          {t('getReleaseAlert')}
        </div>
      )}
    </div>
  );
}
